import { useTranslation } from 'react-i18next'

import type { useRovingGroup } from '@/shared/hooks/useRovingGroup'
import { cn } from '@/shared/lib/cn'

import { NOTIFICATION_TABS } from '../lib/notificationTabs'
import type { NotificationTab } from '../types/notification.types'

const TAB_KEYS: Record<NotificationTab, string> = {
  ACTIVITY: 'notifications.tabs.ACTIVITY',
  NEWS: 'notifications.tabs.NEWS',
}

type Roving = Pick<
  ReturnType<typeof useRovingGroup<NotificationTab>>,
  'getGroupProps' | 'getItemProps'
>

interface Props {
  roving: Roving
  active: NotificationTab
  activityUnread: number
  newsUnread: number
  size?: 'sm' | 'md'
}

/**
 * Pestañas de la campana (spec `2026-11-03-campana-pestanas`): «Tus partidas» con su número de
 * sin leer y «Novedades» con un punto si hay alguna. Teclado con flechas (`useRovingGroup`).
 */
export function NotificationTabs({
  roving,
  active,
  activityUnread,
  newsUnread,
  size = 'sm',
}: Props) {
  const { t } = useTranslation()
  return (
    <div {...roving.getGroupProps()} className="flex border-b-2 border-foreground/10">
      {NOTIFICATION_TABS.map((tab) => {
        const selected = tab === active
        return (
          <button
            key={tab}
            {...roving.getItemProps(tab)}
            className={cn(
              'relative flex min-h-11 flex-1 items-center justify-center gap-1.5 px-3 font-display font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue',
              size === 'md' ? 'text-base' : 'text-sm',
              selected
                ? 'bg-background text-foreground'
                : 'text-muted-foreground hover:bg-yellow/20',
            )}
          >
            {selected && (
              <span aria-hidden="true" className="absolute inset-x-0 bottom-0 h-[3px] bg-red" />
            )}
            {t(TAB_KEYS[tab])}
            {tab === 'ACTIVITY' && activityUnread > 0 && (
              <span className="chip inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red px-1 font-brutal text-[10px] font-bold text-white">
                {activityUnread > 99 ? '99+' : activityUnread}
              </span>
            )}
            {tab === 'NEWS' && newsUnread > 0 && (
              <span className="inline-block size-2 rounded-full bg-blue" aria-hidden="true" />
            )}
            {tab === 'NEWS' && newsUnread > 0 && (
              <span className="sr-only">{t('notifications.tabs.newsUnreadSr')}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}
